// Whose Appointments the canvas is showing, derived from the URL selection and
// the viewer's own reach (NO-467). Unlike the /rds roster scope this is never
// stored: the selection is a transient `?rdId=` because the rows behind it are
// PHI, so a remembered choice would put a patient list on screen unasked.
export type AppointmentScope =
  | { kind: "own" }
  | { kind: "none" }
  | { kind: "rd"; rdId: string };

export function appointmentScopeFor(
  rdId: string | undefined,
  holdsOwnRows: boolean,
): AppointmentScope {
  if (rdId !== undefined) return { kind: "rd", rdId };
  return holdsOwnRows ? { kind: "own" } : { kind: "none" };
}

/** The reserved picker row for the viewer's own schedule; never an RD id. */
export const OWN_APPOINTMENTS_VALUE = "own";

/**
 * The page title for a viewer who can pick an RD. `null` while a selected RD's
 * name has not resolved: the title must then be a placeholder, never the
 * signed-in admin's greeting over someone else's schedule.
 */
export function appointmentScopeTitle(
  scope: AppointmentScope,
  rdName: string | null,
): string | null {
  switch (scope.kind) {
    case "own":
      return "My Appointments";
    case "none":
      return "Select an RD";
    case "rd":
      return rdName;
  }
}

// --- Copy ------------------------------------------------------------------

export type AppointmentUnavailableReason = "provider_unavailable" | "configuration_unavailable";

/**
 * The unavailable state, by reason and by whose schedule it is. An admin is the
 * person who fixes a Provider link, so their copy names the fix; an RD's asks.
 * The RD's name is attribution, never a constructed possessive.
 */
export function appointmentUnavailableCopy(input: {
  reason: AppointmentUnavailableReason;
  viewingOther: boolean;
  rdName: string | null;
  /** The roster resolved and holds no RD with the selected id (a stale link). */
  rdMissing?: boolean;
}): { title: string; description: string } {
  if (input.rdMissing) {
    return {
      title: "RD not found",
      description: "No RD in the roster has this id. The link may be stale; choose an RD from the title.",
    };
  }
  if (input.reason === "configuration_unavailable") {
    return {
      title: "Appointments are unavailable",
      description: "AlwaysAI's Healthie calendar connection needs attention. Ask an engineer to check the calendar configuration.",
    };
  }
  if (input.viewingOther) {
    const who = input.rdName ?? "This RD";
    return {
      title: "Appointments aren't connected",
      description: `${who} has no linked Healthie Provider. Link the RD to their Provider in NDD Admin, then reload.`,
    };
  }
  return {
    title: "Your Appointments are unavailable",
    description: "Ask an administrator to check your Provider link and calendar settings.",
  };
}
